import React, { useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import LinearProgress from '@material-ui/core/LinearProgress';
import { AgGridReact } from 'ag-grid-react';
import type {
  ColumnApi,
  GridOptions,
  GridReadyEvent,
  ICellRendererParams,
} from 'ag-grid-community';
import { useTheme } from '@material-ui/core/styles';

import type {
  AgentGateway,
  PatientConversationSummary,
} from '@mummssoftware/utils';

import {
  conversationQuestions,
  unansweredQuestions,
  useConversationMessages,
  useConversationUnanswered,
} from '../../hooks/useConversationMessages';
import { useCostColumnsShown } from '../../hooks/useShowCost';
import type { ConversationQuestion } from '../../types';
import defaults from '../../translations/en.json';
import {
  getQuestionColumnDefs,
  UNANSWERED_ROW_CLASS,
} from './questionColumnDefs';

/** Rows under their conversation row, in its columns; grows with its questions (no inner scroll). */
const Root = styled.div`
  height: 100%;
  background-color: ${(props) => props.theme.palette.background.paper};

  /* only the conversation rows open on click; these open with their icon */
  .ag-row {
    cursor: default;
  }
  /* a question that got no answer: shown for what it spent, greyed */
  .ag-row.${UNANSWERED_ROW_CLASS} .ag-cell {
    font-style: italic;
    color: ${(props) =>
      props.theme.palette.text.readOnly || props.theme.palette.text.secondary};
  }
`;

const ErrorText = styled.p`
  margin: 0;
  padding: 6px 12px;
  font-size: 0.8rem;
  color: ${(props) => props.theme.palette.error?.main || '#f44336'};
`;

export type ConversationDetailParams = ICellRendererParams & {
  agentGateway: AgentGateway;
  onOpenQuestion: (
    conversation: PatientConversationSummary,
    question: ConversationQuestion,
  ) => void;
  smallWidget?: boolean;
  siteTimezone?: string | null;
};

/** Gives each question column the width of the history column with its colId. */
const matchWidths = (history: ColumnApi, questions: ColumnApi) => {
  history.getAllDisplayedColumns().forEach((column) => {
    const own = questions.getColumn(column.getColId());
    if (own && own.getActualWidth() !== column.getActualWidth()) {
      questions.setColumnWidth(own, column.getActualWidth());
    }
  });
};

/**
 * A history row opened in place: its questions, one row per question and
 * answer, with no header and in the history's columns (each width follows the
 * history column above it, also when that one is resized). Loads when opened.
 */
export const ConversationDetail = ({
  data,
  pinned,
  api: historyApi,
  columnApi: historyColumnApi,
  agentGateway,
  onOpenQuestion,
  smallWidget,
  siteTimezone,
}: ConversationDetailParams) => {
  const {
    props: { grid: gridTheme },
  } = useTheme() as any;
  const conversation = data as PatientConversationSummary;
  // embedFullWidthRows draws this row in the left, center and right sections;
  // only the center one (nothing is pinned) loads and shows the questions
  const isCenter = !pinned;
  const { stored, isLoading, error } = useConversationMessages(
    agentGateway,
    isCenter ? conversation : null,
  );
  const unanswered = useConversationUnanswered(
    agentGateway,
    isCenter ? conversation : null,
  );
  // the grid sorts them by time (startsAt)
  const rows = useMemo(
    () => [
      ...conversationQuestions(stored, conversation),
      ...unansweredQuestions(unanswered),
    ],
    [stored, conversation, unanswered],
  );
  const showCost = useCostColumnsShown();

  const columnDefs = useMemo(
    () =>
      getQuestionColumnDefs({
        onOpenQuestion: (question) => onOpenQuestion(conversation, question),
        smallWidget,
        siteTimezone,
        showCost,
      }),
    [conversation, onOpenQuestion, smallWidget, siteTimezone, showCost],
  );

  const questionsColumnApi = useRef<ColumnApi | null>(null);
  // Follow the history columns: fitted to the widget, or resized by the user.
  useEffect(() => {
    const follow = () => {
      if (questionsColumnApi.current) {
        matchWidths(historyColumnApi, questionsColumnApi.current);
      }
    };
    historyApi.addEventListener('columnResized', follow);
    return () => historyApi.removeEventListener('columnResized', follow);
  }, [historyApi, historyColumnApi]);

  const gridOptions: GridOptions = useMemo(
    () => ({
      // no header: widths come from the history columns, order is oldest first
      defaultColDef: {
        resizable: false,
        sortable: false,
        suppressMenu: true,
      },
      suppressCellSelection: true,
      domLayout: 'autoHeight',
      getRowNodeId: (row: ConversationQuestion) => String(row.id),
      rowClassRules: {
        [UNANSWERED_ROW_CLASS]: (params) =>
          Boolean((params.data as ConversationQuestion | undefined)?.outcome),
      },
      onGridReady: ({ columnApi }: GridReadyEvent) => {
        questionsColumnApi.current = columnApi;
        matchWidths(historyColumnApi, columnApi);
      },
    }),
    [historyColumnApi],
  );

  if (!isCenter) {
    return null;
  }

  let content: React.ReactNode = null;
  if (isLoading) {
    // a thin bar under the conversation row while its questions load
    content = <LinearProgress aria-label={defaults.loadingConversation} />;
  } else if (rows.length > 0 || !error) {
    content = (
      <div className={`${gridTheme.className} light`}>
        <AgGridReact
          gridOptions={gridOptions}
          columnDefs={columnDefs}
          rowData={rows}
          // the history's header above names these columns
          headerHeight={0}
          rowHeight={gridTheme?.rowHeight || 27}
          overlayNoRowsTemplate={`<span>${defaults.noConversations}</span>`}
        />
      </div>
    );
  }

  return (
    <Root data-testid="maia-conversation-questions">
      {error ? <ErrorText role="alert">{error}</ErrorText> : null}
      {content}
    </Root>
  );
};

export default ConversationDetail;
